import {ValidationError} from '../../Error.mjs'
import ArType from './ArType.jsx'
import metaBizNode from '../../metaBizNode.mjs'

const ar2name = Object.fromEntries(
    ArType
        .flat()
        .filter(a => Array.isArray(a))
)

const biz2name = Object.fromEntries(
    Object.entries(metaBizNode).map(
        ([type, {name}]) => [type, name]
    )
)

/**
 * 把子节点按要求分组
 */
const categorizeChildren = (node, preds) => {
    const categorized = Array.from(
        new Array(preds.length + 1),
        () => []
    )

    for (const n of node.children) {
        (() => {
            for (const [i, pred] of preds.entries()) {
                if (pred(n)) {
                    categorized[i].push(n)
                    return
                }
            }

            categorized.at(-1).push(n)
        })()
    }

    return categorized
}

/**
 * 获取节点的描述类型名称
 */
const getTypeName = ar => {
    const {arTypeCode} = ar.data
    return ar2name[arTypeCode]
}

/**
 * 判断目标描述节点是否为指定的类型
 */
const isArType = (node, type) => {
    const {arTypeCode} = node.data

    if ('string' === typeof type) {
        return type === arTypeCode
    }
    else {
        return type.test(arTypeCode)
    }
}

/**
 * 判断目标节点是否为指定的类型
 */
const isBizType = (node, type) => {
    const {bizNodeType} = node.data

    if ('String' === type) {
        return type === bizNodeType
    }
    else {
        return type.test(bizNodeType)
    }
}

/**
 * 判断节点是否为数据实体或数据表
 */
const isDataNode = node => {
    const {bizNodeType, ruleType} = node.data

    return (
        'DT' === bizNodeType ||

        (
            'BDRULE' === bizNodeType &&
            'DE' === ruleType
        )
    )
}

/**
 * 判断节点是否为语句起始节点
 */
const isStatement = node => isArType(node, /^(ALG|CAT|CASE|CLOSE|DB_[CDRU]|DISABLE|ENABLE|EXEC|EXPORT|HIDE|LOOP|POP|PRINT|REFRESH|SET_VAR|SHOW|OUT|UI|VAR(_LIST)?|VERIFY)$/)

/**
 * 检查节点标题是否为合法的赋值语句
 */
const checkAssign = node => {
    const {arName} = node.data
    const t = getTypeName(node)

    // @see 变量声明类语句
    if (! /^\$/.test(arName)) {
        throw new ValidationError(
            `${t}的标题必须以 $ 为前缀`,
            node
        )
    }

    if (/^\$$/.test(arName)) {
        throw new ValidationError(
            `${t}的标题除 $ 前缀外必须有其他字符`,
            node
        )
    }

    if (! /^\$[_a-zA-Z]/.test(arName)) {
        throw new ValidationError(
            `${t}的标题 $ 前缀的下一个字符只能是下划线或字母`,
            node
        )
    }

    // @see 数据字段集合变量元素赋值语句
    // @see 数据实体单体变量字段赋值语句
    if (! /^\$[_a-zA-Z]\w*(\[(\d+|\$[_a-zA-Z]\w*|'.*'|".*")\]|\.[_a-zA-Z]\w*)?$/.test(arName)) {
        throw new ValidationError(
            `${t}的标题必须是某个单体或集合变量的名称`,
            node
        )
    }
}

/**
 * 检查节点标题是否为合法的声明
 *
 * 一个合法的声明以 $ 作为前缀，之后是一个合法的标识符
 */
const checkDeclare = node => {
    const {arName} = node.data
    const t = getTypeName(node)

    // @see 变量声明类语句
    if (! /^\$/.test(arName)) {
        throw new ValidationError(
            `${t}的标题必须以 $ 为前缀`,
            node
        )
    }

    if (/^\$$/.test(arName)) {
        throw new ValidationError(
            `${t}的标题除 $ 前缀外必须有其他字符`,
            node
        )
    }

    if (! /^\$[_a-zA-Z]/.test(arName)) {
        throw new ValidationError(
            `${t}的标题 $ 前缀的下一个字符只能是下划线或字母`,
            node
        )
    }

    if (! /^\$[_a-zA-Z]\w*$/.test(arName)) {
        throw new ValidationError(
            `${t}的标题除 $ 前缀外只能是下划线、字母或数字`,
            node
        )
    }
}

/**
 * 检查节点标题是否为合法的标识符
 *
 * 为尽可能的兼容各种编程语言，标识符限制为下划线或英文字母开头，
 * 其余部分只能是下划线、英文字母或数字
 */
const checkIdentifier = node => {
    const {arName} = node.data
    const t = getTypeName(node)

    if (/^$/.test(arName)) {
        throw new ValidationError(
            `${t}的标题不能为空`,
            node
        )
    }

    if (! /^[\w^\d]/.test(arName)) {
        throw new ValidationError(
            `${t}的标题第一个字符只能是下划线或字母`,
            node
        )
    }

    if (! /^[\w^\d]\w*$/.test(arName)) {
        throw new ValidationError(
            `${t}的标题只能下划线、字母或数字`,
            node
        )
    }
}

/**
 * 检查节点是否填写了标题
 */
const checkHasTitle = node => {
    const {arName} = node.data
    const t = getTypeName(node)

    if (! arName) {
        throw new ValidationError(
            `${t}必须填写标题`,
            node
        )
    }
}

/**
 * 检查节点是否正好有一个子节点
 */
const checkOneChild = (node, check, typeName) => {
    const children = [...node.children]
    const t = typeName ?? getTypeName(node)

    if (1 === children.length) {
        check?.(children[0])
    }
    else {
        throw new ValidationError(
            `${t}必须有且仅有 1 个子节点`,
            node
        )
    }
}

/**
 * 检查节点是否正好有一个模件子节点
 */
const checkOneDesignChild = (node, check, typeName) => {
    checkOneChild(
        node,

        n => {
            const t = typeName ?? getTypeName(node)

            if (! n.data.pkid) {
                throwChildren(t, ['模件'], n)
            }

            check?.(n)
        },

        typeName
    )
}

/**
 * 检查节点是否至少有一个子节点
 */
const checkOneOrMoreChild = (node, check, typeName) => {
    const children = [...node.children]
    const t = typeName ?? getTypeName(node)

    if (0 < children.length) {
        check?.(children)
    }
    else {
        throw new ValidationError(
            `${t}必须至少有 1 个子节点`,
            node
        )
    }
}

/**
 * 检查节点是否只包含语句类子节点
 */
const checkStatements = (node, typeName) => {
    const t = typeName ?? getTypeName(node)

    for (const n of node.children) {
        if (! isStatement(n)) {
            throwChildren(t, ['语句类'], n)
        }
    }
}

/**
 * 检查节点是否没有子节点
 */
const checkZeroChild = (node, typeName) => {
    const children = [...node.children]
    const t = typeName ?? getTypeName(node)

    if (0 < children.length) {
        throw new ValidationError(
            `${t}不能有任何子节点`,
            node
        )
    }
}

/**
 * 检查节点是否最多有一个子节点
 */
const checkZeroOrOneChild = (node, check, typeName) => {
    const children = [...node.children]
    const t = typeName ?? getTypeName(node)

    if (1 < children.length) {
        throw new ValidationError(
            `${t}只能有 0 或 1 个子节点`,
            node
        )
    }

    const [n] = children

    if (n) {
        check(n)
    }
}

/**
 * 检查节点是否最多有一个模件子节点
 */
const checkZeroOrOneDesignChild = (node, check, typeName) => {
    checkZeroOrOneChild(
        node,

        n => {
            const t = typeName ?? getTypeName(node)

            if (! n.data.pkid) {
                throwChildren(t, ['模件'], n)
            }

            check?.(n)
        },

        typeName
    )
}

/**
 * 按汉语的习惯拼接并列的词组
 */
const join = words => {
    if (1 < words.length) {
        return `${words.slice(0, -1).join('、')}或${words.at(-1)}`
    }
    else {
        return words[0]
    }
}

/**
 * 抛出因包含不合法子节点导致的异常
 */
const throwChildren = (parentTypeName, expectedTypeNames, errorNode) => {
    throw new ValidationError(
        `${parentTypeName}只能有${join(expectedTypeNames)}子节点`,
        errorNode
    )
}

/**
 * 抛出因子节点数量不符（有且仅有一个）或子节点不合法导致的异常
 */
const throwOneChild = (parentTypeName, expectedTypeNames, errorNode) => {
    throw new ValidationError(
        `${parentTypeName}必须有且仅有一个${join(expectedTypeNames)}子节点`,
        errorNode
    )
}

/**
 * 抛出因子节点数量不符（至少有一个）或子节点不合法导致的异常
 */
const throwOneOrMoreChild = (parentTypeName, expectedTypeNames, errorNode) => {
    throw new ValidationError(
        `${parentTypeName}必须至少有一个${join(expectedTypeNames)}子节点`,
        errorNode
    )
}

/**
 * 抛出因子节点数量不符（至多有一个）或子节点不合法导致的异常
 */
const throwZeroOrOneChild = (parentTypeName, expectedTypeNames, errorNode) => {
    throw new ValidationError(
        `${parentTypeName}至多只能有一个${join(expectedTypeNames)}子节点`,
        errorNode
    )
}

export const lintForCode = (map, entryNode) => {
    /**
     * @see 软件功能描述语句
     */
    checkStatements(entryNode, '处理目标')

    const lint = {
        /**
         * @see 调用能力语句
         */
        ALG(node) {
            const t = getTypeName(node)

            const [algs, , others] = categorizeChildren(node, [
                n => isBizType(n, /^(BDLOGIC|LSI|UE|UIALG)$/),
                n => isArType(n, /^SET_(IP|VAR)$/),
            ])

            if (0 < others.length) {
                throwChildren(
                    t,

                    [
                        biz2name.BDLOGIC,
                        biz2name.UE_,
                        biz2name.LSI,
                        ar2name.SET_IP,
                        ar2name.SET_VAR,
                    ],

                    others[0]
                )
            }

            if (1 !== algs.length) {
                throwOneChild(
                    t,
                    [biz2name.BDLOGIC, biz2name.UE_, biz2name.LSI],
                    node
                )
            }
        },

        /**
         * @see 分支语句
         */
        CASE(node) {
            checkHasTitle(node)
            checkStatements(node)
        },

        /**
         * @see 组别语句
         */
        CAT(node) {
            checkStatements(node)
        },

        /**
         * @see 关闭界面语句
         */
        CLOSE(node) {
            checkZeroChild(node)
        },

        /**
         * @see 新建库表记录语句
         */
        DB_C(node) {
            const t = getTypeName(node)

            const [designs, setDfs, others] = categorizeChildren(node, [
                isDataNode,
                n => isArType(n, 'SET_DF'),
            ])

            if (0 < others.length) {
                throwChildren(
                    t,
                    [biz2name.DT, biz2name.BDRULE_1, ar2name.SET_DF],
                    others[0]
                )
            }

            if (1 !== designs.length) {
                throwOneChild(t, [biz2name.DT, biz2name.BDRULE_1], node)
            }

            if (1 !== setDfs.length) {
                throwOneChild(t, [ar2name.SET_DF], node)
            }
        },

        /**
         * @see 删除库表记录语句
         */
        DB_D(node) {
            const t = getTypeName(node)

            const [
                designs,
                recFilterReqs,
                others,
            ] = categorizeChildren(node, [
                isDataNode,
                n => isArType(n, 'REC_FILTER_REQ'),
            ])

            if (0 < others.length) {
                throwChildren(
                    t,
                    [biz2name.DT, biz2name.BDRULE_1, ar2name.REC_FILTER_REQ],
                    others[0]
                )
            }

            if (1 !== designs.length) {
                throwOneChild(
                    t,
                    [biz2name.DT, biz2name.BDRULE_1],
                    node
                )
            }

            if (1 !== recFilterReqs.length) {
                throwOneChild(
                    t,
                    [ar2name.REC_FILTER_REQ],
                    node
                )
            }
        },

        /**
         * @see 查询库表记录语句
         */
        DB_R(node) {
            const t = getTypeName(node)

            const [
                designs,
                recFilterReqs,
                recGroupReqs,
                grpFilterReqs,
                sortReqs,
                others,
            ] = categorizeChildren(node, [
                isDataNode,
                n => isArType(n, 'REC_FILTER_REQ'),
                n => isArType(n, 'REC_GROUP_REQ'),
                n => isArType(n, 'GRP_FILTER_REQ'),
                n => isArType(n, 'SORT_REQ'),
            ])

            if (0 < others.length) {
                throwChildren(
                    t,

                    [
                        biz2name.DT,
                        biz2name.BDRULE_1,
                        ar2name.REC_FILTER_REQ,
                        ar2name.REC_GROUP_REQ,
                        ar2name.GRP_FILTER_REQ,
                        ar2name.SORT_REQ,
                    ],

                    others[0]
                )
            }

            if (0 === designs.length) {
                throwOneOrMoreChild(t, [biz2name.DT, biz2name.BDRULE_1], node)
            }

            if (1 !== recFilterReqs.length) {
                throwOneChild(t, [ar2name.REC_FILTER_REQ], node)
            }

            if (1 < recGroupReqs.length) {
                throwZeroOrOneChild(t, [ar2name.REC_GROUP_REQ], node)
            }

            if (1 < grpFilterReqs.length) {
                throwZeroOrOneChild(t, [ar2name.GRP_FILTER_REQ], node)
            }

            if (1 < sortReqs.length) {
                throwZeroOrOneChild(t, [ar2name.SORT_REQ], node)
            }
        },

        /**
         * @see 更新库表记录语句
         */
        DB_U(node) {
            const t = getTypeName(node)

            const [
                designs,
                setDfs,
                recFilterReqs,
                others,
            ] = categorizeChildren(node, [
                isDataNode,
                n => isArType(n, 'SET_DF'),
                n => isArType(n, 'REC_FILTER_REQ'),
            ])

            if (0 < others.length) {
                throwChildren(
                    t,

                    [
                        biz2name.DT,
                        biz2name.BDRULE_1,
                        ar2name.SET_DF,
                        ar2name.REC_FILTER_REQ,
                    ],

                    others[0]
                )
            }

            if (1 !== designs.length) {
                throwOneChild(t, [biz2name.DT, biz2name.BDRULE_1], node)
            }

            if (1 !== setDfs.length) {
                throwOneChild(t, [ar2name.SET_DF], node)
            }

            if (1 !== recFilterReqs.length) {
                throwOneChild(t, [ar2name.REC_FILTER_REQ], node)
            }
        },

        DC(node) {
            const t = getTypeName(node)

            // @see 界面控件赋值展示语句
            if (isArType(node.parent, 'OUT')) {
                checkZeroOrOneChild(node, n => {
                    if (! isArType(n, 'GET')) {
                        throwZeroOrOneChild(t, [ar2name.GET], n)
                    }
                })
            }
            // @see 刷新控件语句
            // @see 禁用控件语句
            // @see 启用控件语句
            // @see 隐藏控件语句
            // @see 显示控件语句
            else if (isArType(node.parent, /^(DISABLE|ENABLE|HIDE|REFRESH|SHOW)$/)) {
                checkZeroChild(node)
            }
        },

        DC_M_MODE(node) {
            this.DC(node)
        },

        DC_HIDDEN(node) {
            this.DC(node)
        },

        DC_BUTTON(node) {
            this.DC(node)
        },

        DC_BUTTON_MENU(node) {
            this.DC(node)
        },

        DC_LINK(node) {
            this.DC(node)
        },

        DC_NAV_TABS(node) {
            this.DC(node)
        },

        DC_READ(node) {
            this.DC(node)
        },

        DC_EDIT(node) {
            this.DC(node)
        },

        DC_EDIT_M(node) {
            this.DC(node)
        },

        DC_SELECT(node) {
            this.DC(node)
        },

        DC_SEL_EDIT(node) {
            this.DC(node)
        },

        DC_CHECK(node) {
            this.DC(node)
        },

        DC_RADIO(node) {
            this.DC(node)
        },

        DC_DATE(node) {
            this.DC(node)
        },

        DC_PIC(node) {
            this.DC(node)
        },

        DC_LIST(node) {
            this.DC(node)
        },

        DC_EVENT(node) {
            this.DC(node)
        },

        /**
         * @see 字段赋值子句
         */
        DF(node) {
            const t = getTypeName(node)
            checkHasTitle(node)

            checkOneChild(node, n => {
                if (! isArType(n, 'GET')) {
                    throwOneChild(t, [ar2name.GET], node)
                }
            })
        },

        /**
         * @see 禁用控件语句
         */
        DISABLE(node) {
            this.OUT(node)
        },

        /**
         * @see 启用控件语句
         */
        ENABLE(node) {
            this.OUT(node)
        },

        /**
         * @see 通用处理语句
         */
        EXEC(node) {
            const [masters, slaves, others] = categorizeChildren(node, [
                n => isBizType(n, /^(BDLOGIC|LSI|UE|UIALG)$/),
                n => isArType(n, /^SET_(IP|VAR)$/),
            ])

            // @see 调用语句
            if (
                0 < masters.length ||
                0 < slaves.length
            ) {
                const t = getTypeName(node)

                if (0 < others.length) {
                    throwChildren(
                        t,

                        [
                            biz2name.BDLOGIC,
                            biz2name.UE_,
                            biz2name.LSI,
                            ar2name.SET_IP,
                            ar2name.SET_VAR,
                        ],

                        others[0]
                    )
                }

                if (1 !== masters.length) {
                    throwOneChild(
                        t,
                        [biz2name.BDLOGIC, biz2name.UE_, biz2name.LSI],
                        node
                    )
                }
            }
            // @see 通用处理语句
            else {
                checkZeroOrOneDesignChild(node)
            }
        },

        /**
         * @see 导出文件语句
         */
        EXPORT(node) {
            this.PRINT(node)
        },

        /**
         * @see 数据字段变量赋值语句
         * @see 字段赋值子句
         * @see 查询库表记录子句
         * @see 控件赋值子句
         */
        GET(node) {
            const t = getTypeName(node)

            // @see 字段赋值子句
            if (isArType(node.parent, 'DF')) {
                checkZeroOrOneDesignChild(node)
            }
            // @see 数据字段变量赋值语句
            // @see 查询库表记录子句
            else if (isArType(node.parent, 'SET_VAR')) {
                checkOneChild(node, n => {
                    if (
                        ! n.data.pkid &&
                        ! isArType(n, 'DB_R')
                    ) {
                        throwOneChild(t, ['模件', ar2name.DB_R], node)
                    }
                })
            }
            // @see 控件赋值子句
            else if (isArType(node.parent, /^DC($|_.+)/)) {
                checkZeroChild(node)
            }
        },

        /**
         * @see 出参赋值子句
         */
        GET_OP(node) {
            checkZeroChild(node)
        },

        /**
         * @see 目标分组筛选子句
         */
        GRP_FILTER_REQ(node) {
            checkHasTitle(node)
            checkZeroChild(node)
        },

        /**
         * @see 隐藏控件语句
         */
        HIDE(node) {
            this.OUT(node)
        },

        /**
         * @see 通用循环语句
         * @see 数据集合遍历循环语句
         */
        LOOP(node) {
            checkHasTitle(node)
            checkStatements(node)
        },

        /**
         * @see 界面控件赋值展示语句
         */
        OUT(node) {
            const t = getTypeName(node)

            checkOneOrMoreChild(node, ns => {
                for (const n of ns) {
                    if (! isArType(n, /^DC($|_.+)/)) {
                        throwOneOrMoreChild(t, ['界面控件类'], node)
                    }
                }
            })
        },

        /**
         * @see 弹出界面语句
         */
        POP(node) {
            const t = getTypeName(node)

            const [designs, , others] = categorizeChildren(node, [
                n => isBizType(n, /^U[IV]$/),
                n => isArType(n, /^SET_(IP|VAR)$/),
            ])

            if (0 < others.length) {
                throwChildren(
                    t,
                    [biz2name.UI, biz2name.UV, ar2name.SET_IP, ar2name.SET_VAR],
                    others[0]
                )
            }

            if (1 !== designs.length) {
                throwOneChild(
                    t,
                    [biz2name.UI, biz2name.BDRULE_1],
                    node
                )
            }
        },

        /**
         * @see 打印语句
         */
        PRINT(node) {
            const t = getTypeName(node)

            for (const n of node.children) {
                if (! isBizType(n, /^(DOC|PIC|URL)$/)) {
                    throwChildren(t, ['资源类'], n)
                }
            }
        },

        /**
         * @see 目标记录筛选子句
         */
        REC_FILTER_REQ(node) {
            checkHasTitle(node)
            checkZeroChild(node)
        },

        /**
         * @see 分组子句
         */
        REC_GROUP_REQ(node) {
            checkHasTitle(node)
            checkZeroChild(node)
        },

        /**
         * @see 刷新控件语句
         */
        REFRESH(node) {
            this.OUT(node)
        },

        /**
         * @see 数据实体赋值子句（模式二）
         * @see 新建库表记录子句
         * @see 更新库表记录语句
         */
        SET_DF(node) {
            const t = getTypeName(node)

            for (const n of node.children) {
                if (! isArType(n, 'DF')) {
                    throwChildren(t, [ar2name.DF], n)
                }
            }
        },

        /**
         * @see 入参赋值子句
         */
        SET_IP(node) {
            checkHasTitle(node)
        },

        /**
         * @see 出参赋值子句
         * @see 变量赋值语句
         * @see 弹出界面子句
         */
        SET_VAR(node) {
            const t = getTypeName(node)
            checkAssign(node)

            // @see 调用子句
            // @see 弹出界面子句
            if (isArType(node.parent, /^(EXEC|POP)$/)) {
                // @see 出参赋值子句
                checkOneChild(node, n => {
                    if (! isArType(n, 'GET_OP')) {
                        throwChildren(t, [ar2name.GET_OP], n)
                    }
                })
            }
            // @see 变量赋值语句
            else {
                checkOneChild(node, n => {
                    if (! isArType(n, /^(GET|SET_DF)$/)) {
                        throwChildren(t, [ar2name.GET, ar2name.SET_DF], n)
                    }
                })
            }
        },

        /**
         * @see 显示控件语句
         */
        SHOW(node) {
            this.OUT(node)
        },

        /**
         * @see 排序子句
         */
        SORT_REQ(node) {
            checkHasTitle(node)
            checkZeroChild(node)
        },

        /**
         * @see 打开界面语句
         */
        UI(node) {
            const t = getTypeName(node)

            const [designs, , others] = categorizeChildren(node, [
                n => isBizType(n, /^U[IV]$/),
                n => isArType(n, 'SET_IP'),
            ])

            if (0 < others.length) {
                throwChildren(
                    t,
                    [biz2name.UI, biz2name.UV, ar2name.SET_IP],
                    others[0]
                )
            }

            if (1 !== designs.length) {
                throwOneChild(
                    t,
                    [biz2name.UI, biz2name.BDRULE_1],
                    node
                )
            }
        },

        /**
         * @see 单体变量声明语句
         */
        VAR(node) {
            const t = getTypeName(node)
            checkDeclare(node)

            checkZeroOrOneChild(node, n => {
                const {bizNodeType, ruleType} = n.data

                // @see 数据实体单体变量声明语句
                // @see 数据对象单体变量声明语句
                if (! (
                    'BDRULE' === bizNodeType &&
                    /^(DE|IS)$/.test(ruleType)
                )) {
                    throwZeroOrOneChild(
                        t,
                        [biz2name.BDRULE_1, biz2name.BDRULE_3],
                        node
                    )
                }
            })

            let n = node.prevSibling
            const {arName: n1} = node.data

            while (n) {
                const {arName: n2} = n.data

                if (
                    n !== node &&
                    isArType(n, /^(VAR(_LIST)?)$/) &&
                    n1 === n2
                ) {
                    throw new ValidationError('重复声明变量', node)
                }
            }
        },

        /**
         * @see 集合变量声明语句
         */
        VAR_LIST(node) {
            this.VAR(node)
        },

        /**
         * @see 校验语句
         */
        VERIFY(node) {
            checkHasTitle(node)
            checkZeroChild(node)
        },
    }

    for (const n of map.dfs(entryNode.children)) {
        lint[n.data.arTypeCode]?.(n)
    }
}

export const lintForDoc = (map, entryNode) => {
}
